import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  HiOutlineXMark,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlinePhoto,
} from "react-icons/hi2";
import {
  authService,
  safetyObservationService,
  plantService,
  lineService,
  masterDataService,
  userService,
} from "../services";
import siteLayoutService from "../services/siteLayoutService";
import { usePermissions } from "../hooks/usePermissions";
import PageLoading from "../components/common/PageLoading";
import "./SafetyObservationEntry.css";

const SafetyObservationEntry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tenantId = authService.getTenantId();
  const currentUser = authService.getUser();
  const { hasPermission } = usePermissions();
  const isEditMode = !!id;

  // Permission checks for Safety Observation Round
  const canCreate = hasPermission("SafetyObservationRound.Create");
  const canEdit = hasPermission("SafetyObservationRound.Edit");

  console.log("canCreate", canCreate);
  console.log("canEdit", canEdit);

  // Permission checks for Safety Observation Items
  const canViewItem = hasPermission("SafetyObservationItem.View");
  const canCreateItem = hasPermission("SafetyObservationItem.Create");
  const canEditItem = hasPermission("SafetyObservationItem.Edit");
  const canDeleteItem = hasPermission("SafetyObservationItem.Delete");

  console.log("canViewItem", canViewItem);
  console.log("canCreateItem", canCreateItem);
  console.log("canEditItem", canEditItem);
  console.log("canDeleteItem", canDeleteItem);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Master data
  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [lines, setLines] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [siteLayout, setSiteLayout] = useState(null);
  const [layoutItems, setLayoutItems] = useState({});
  const [selectedLayout, setSelectedLayout] = useState({});
  const [sboSettings, setSBOSettings] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    observationDate: new Date().toISOString().split("T")[0],
    observationTime: "",
    endTime: "",
    observationDuration: "",
    actionSlaTime: "60", // Default 60 minutes
    plantId: "",
    departmentId: "",
    lineId: "",
    area: "",
    companyEmployeeCount: "0",
    contractorEmployeeCount: "0",
    companyEmployeeNames: [],
    contractorEmployeeNames: [],
  });

  const [addNames, setAddNames] = useState(false);

  const isCompleted = formData.statusName === "Completed";

  const [observers, setObservers] = useState([]);
  // Combined arrays with observationType field
  const [safeObservations, setSafeObservations] = useState([]);
  const [unsafeObservations, setUnsafeObservations] = useState([]);
  // Store subcategories for each item (keyed by item index and categoryId)

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (tenantId) {
      // Check permissions for loading data
      if (isEditMode && !canEdit) {
        setError(
          "You do not have permission to edit Safety Observation rounds",
        );
        setLoading(false);
        return;
      }
      if (!isEditMode && !canCreate) {
        setError(
          "You do not have permission to create Safety Observation rounds",
        );
        setLoading(false);
        return;
      }
      loadInitialData();
    } else {
      setError("Please login to work with Safety Observations");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, isEditMode, canCreate, canEdit]);

  // Fetch Site Layout when plant changes
  useEffect(() => {
    if (formData.plantId) {
      fetchSiteLayout(formData.plantId);
    } else {
      setSiteLayout(null);
      setLayoutItems({});
      setSelectedLayout({});
    }
  }, [formData.plantId]);

  /*
   * Fetch Site Layout when plant changes.
   * Debugging note: If siteLayout renders as "Area", it means layoutData is null or level1 is empty.
   */
  const fetchSiteLayout = async (plantId) => {
    if (!plantId) return;
    try {
      console.log(
        `[SafetyObservationEntry] Fetching layout for plantId: ${plantId} (type: ${typeof plantId})`,
      );

      // Check if service is defined
      if (!siteLayoutService) {
        console.error(
          "[SafetyObservationEntry] siteLayoutService is undefined!",
        );
        return;
      }

      // Fetch layout structure
      const layoutData = await siteLayoutService.getSiteLayout(plantId);
      console.log("[SafetyObservationEntry] Received layoutData:", layoutData);

      setSiteLayout(layoutData || null);

      if (layoutData && layoutData.level1) {
        // Optimization: Only fetch Level 1 initially
        console.log(`[SafetyObservationEntry] Fetching items for level 1`);
        const level1Items = await siteLayoutService.getLevelItems(plantId, 1);
        setLayoutItems(prev => ({ ...prev, [1]: level1Items || [] }));
      } else {
        console.warn(
          "[SafetyObservationEntry] layoutData is missing level1 or is null. Showing Area field.",
        );
        setLayoutItems({});
      }
    } catch (error) {
      console.error(
        "[SafetyObservationEntry] Error in fetchSiteLayout:",
        error,
      );
      setLayoutItems({});
    }
  };

  const initializeLayoutFromArea = async (plantId, areaName) => {
    if (!plantId || !areaName) return;

    try {
      let foundItem = null;
      let foundLevel = 0;

      const levels = [1, 2, 3, 4, 5, 6];
      const searchPromises = levels.map((level) =>
        siteLayoutService
          .searchLevelItems(plantId, level, areaName)
          .then((items) => ({ level, items: items || [] }))
          .catch(() => ({ level, items: [] })),
      );

      const results = await Promise.all(searchPromises);

      for (const res of results) {
        const match = res.items.find((i) => i.name === areaName);
        if (match) {
          foundItem = match;
          foundLevel = res.level;
          break;
        }
      }

      if (foundItem) {
        const newSelected = {};
        const newItems = {};

        newSelected[foundLevel] = foundItem.id;

        let curr = foundItem;
        let lvl = foundLevel;

        while (lvl > 1 && curr.parentId) {
          const parent = await siteLayoutService.getLevelItemById(curr.parentId);
          if (parent) {
            newSelected[lvl - 1] = parent.id;
            curr = parent;
            lvl--;
          } else {
            break;
          }
        }

        const fetchPromises = [];

        fetchPromises.push(
          siteLayoutService.getLevelItems(plantId, 1).then((items) => {
            newItems[1] = items || [];
          }),
        );

        for (let l = 2; l <= foundLevel; l++) {
          fetchPromises.push(
            siteLayoutService.getLevelItems(plantId, l).then((items) => {
              newItems[l] = items || [];
            }),
          );
        }

        if (foundLevel < 6) {
          fetchPromises.push(
            siteLayoutService.getLevelItems(plantId, foundLevel + 1).then((items) => {
              newItems[foundLevel + 1] = items || [];
            }),
          );
        }

        await Promise.all(fetchPromises);

        setLayoutItems((prev) => ({ ...prev, ...newItems }));
        setSelectedLayout(newSelected);
      }
    } catch (error) {
      console.error("Error restoring layout:", error);
    }
  };

  const handleLayoutChange = async (level, itemId) => {
    const nextLevel = level + 1;

    // Update selected layout
    const newSelected = { ...selectedLayout, [level]: itemId };
    // Clear subsequent levels from selection
    for (let i = nextLevel; i <= 6; i++) {
      delete newSelected[i];
    }
    setSelectedLayout(newSelected);

    // Update form data with the most specific selected area name
    const selectedItem = (layoutItems[level] || []).find(
      (item) => String(item.id || item.itemId) === String(itemId),
    );
    if (selectedItem) {
      setFormData((prev) => ({
        ...prev,
        area: selectedItem.name,
      }));
    }

    // Lazy load next level items if needed
    if (itemId && siteLayout && siteLayout[`level${nextLevel}`]) {
      if (!layoutItems[nextLevel] || layoutItems[nextLevel].length === 0) {
        try {
          const items = await siteLayoutService.getLevelItems(
            formData.plantId,
            nextLevel,
          );
          setLayoutItems((prev) => ({ ...prev, [nextLevel]: items || [] }));
        } catch (err) {
          console.error(`Error fetching level ${nextLevel} items:`, err);
        }
      }
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        plantsData,
        departmentsData,
        linesData,
        usersData,
        categoriesData,
        agenciesData,
        settingsData,
      ] = await Promise.all([
        plantService.getAllPlants(),
        masterDataService.getDepartments?.(tenantId) || Promise.resolve([]),
        lineService.getAllLines(),
        userService.getAllUsers().catch((err) => {
          console.error("Error loading users:", err);
          return [];
        }),
        safetyObservationService.getCategories().catch((err) => {
          console.error("Error loading categories:", err);
          return [];
        }),
        masterDataService
          .getContractorAgencies(tenantId, "Active")
          .catch((err) => {
            console.error("Error loading agencies:", err);
            return [];
          }),
        safetyObservationService.getSBOSettings().catch((err) => {
          console.error("Error loading settings:", err);
          return {};
        }),
      ]);

      setPlants(plantsData || []);
      setDepartments(departmentsData || []);
      setLines(linesData || []);
      setUsers(usersData || []);
      setCategories(categoriesData || []);
      setAgencies(agenciesData || []);
      setSBOSettings(settingsData || {});

      // Auto-select plant if only 1 exists (for new entries)
      if (!isEditMode && plantsData?.length === 1) {
          setFormData(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      if (isEditMode) {
        await loadObservationRound();
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadObservationRound = async () => {
    try {
      const round =
        await safetyObservationService.getSafetyObservationRoundById(id);

      setFormData({
        observationDate: round.observationDate
          ? round.observationDate.split("T")[0]
          : "",
        observationTime: round.observationTime || "",
        endTime: round.endTime || "",
        observationDuration: round.observationDuration?.toString() || "",
        actionSlaTime: round.actionSlaTime?.toString() || "60",
        plantId: round.plantId?.toString() || "",
        departmentId: round.departmentId?.toString() || "",
        lineId: round.lineId?.toString() || "",
        area: round.area || "",
        statusName: round.statusName || "",
        companyEmployeeCount: round.companyEmployeeCount?.toString() || "0",
        contractorEmployeeCount:
          round.contractorEmployeeCount?.toString() || "0",
        companyEmployeeNames: round.companyEmployeeNames || [],
        contractorEmployeeNames: round.contractorEmployeeNames || [],
      });

      if (round.plantId) {
        await fetchSiteLayout(round.plantId);
        if (round.area && round.area !== "General Area") {
          await initializeLayoutFromArea(round.plantId, round.area);
        }
      }

      if (
        (round.companyEmployeeNames && round.companyEmployeeNames.length > 0) ||
        (round.contractorEmployeeNames &&
          round.contractorEmployeeNames.length > 0)
      ) {
        setAddNames(true);
      }
      if (round.observers) {
        setObservers(
          round.observers.map((o) => ({
            observerUserId: o.observerUserId,
            observerRole: o.observerRole || "",
          })),
        );
      }

      if (round.items) {
        const items = round.items;
        // Combine safe items
        const safeItems = items.filter(
          (i) =>
            i.observationType === "Safe Act" ||
            i.observationType === "Safe Condition",
        );
        setSafeObservations(
          safeItems.map((item) => ({
            observationType: item.observationType,
            categoryId: item.categoryId?.toString() || "",
            subCategoryId: item.subCategoryId?.toString() || "",
            description: item.description || "",
            observerType: item.observerType || "Company Employee",
            observerName: item.observerName || "",
            observerUserId: item.observerUserId?.toString() || "",
            safetyScore: item.safetyScore?.toString() || "",
            contractorAgencyName: item.contractorAgencyName || "",
            attachments: item.attachments || [],
          })),
        );

        // Combine unsafe items
        const unsafeItems = items.filter(
          (i) =>
            i.observationType === "Unsafe Act" ||
            i.observationType === "Unsafe Condition",
        );
        setUnsafeObservations(
          unsafeItems.map((item) => ({
            itemId: item.itemId,
            observationType: item.observationType,
            categoryId: item.categoryId?.toString() || "",
            subCategoryId: item.subCategoryId?.toString() || "",
            description: item.description || "",
            observerType: item.observerType || "Company Employee",
            observerName: item.observerName || "",
            observerUserId: item.observerUserId?.toString() || "",
            actionDescription: item.actionDescription || "",
            responsiblePersonId: item.responsiblePersonId?.toString() || "",
            targetCompletionTime: item.targetCompletionTime
              ? item.targetCompletionTime.split("T")[0]
              : "",
            riskLevel: item.riskLevel || "",
            safetyScore: item.safetyScore?.toString() || "",
            itemStatus: item.itemStatus || "Open",
            contractorAgencyName: item.contractorAgencyName || "",
            linkedCapaId: item.linkedCapaId || null,
            attachments: item.attachments || [],
          })),
        );
      }
    } catch (err) {
      console.error("Error loading observation round:", err);
      setError("Failed to load observation round. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "plantId") {
      setLayoutItems({});
      setSelectedLayout({});
      setFormData((prev) => ({
        ...prev,
        area: "",
        [name]: value,
      }));
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };

      // Auto-calculate duration if start window or end time changes
      if (name === "observationTime" || name === "endTime") {
        const startTime =
          name === "observationTime" ? value : prev.observationTime;
        const endTime = name === "endTime" ? value : prev.endTime;

        if (startTime && endTime) {
          const startArr = startTime.split(":").map(Number);
          const endArr = endTime.split(":").map(Number);

          let startMins = startArr[0] * 60 + startArr[1];
          let endMins = endArr[0] * 60 + endArr[1];

          // Handle overnight duration (if end time is before start time)
          if (endMins < startMins) {
            endMins += 24 * 60;
          }

          updatedData.observationDuration = (endMins - startMins).toString();
        }
      }

      return updatedData;
    });
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNameSelection = (e, field) => {
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOptions,
      // Auto-update count
      [field === "companyEmployeeNames"
        ? "companyEmployeeCount"
        : "contractorEmployeeCount"]: selectedOptions.length.toString(),
    }));
  };

  const addObserver = () => {
    setObservers([...observers, { observerUserId: "", observerRole: "" }]);
  };

  const removeObserver = (index) => {
    setObservers(observers.filter((_, i) => i !== index));
  };

  const updateObserver = (index, field, value) => {
    const updated = [...observers];
    updated[index] = { ...updated[index], [field]: value };
    setObservers(updated);
  };

  const addSafeObservation = () => {
    if (!canCreateItem || isCompleted) {
      setError(
        isCompleted
          ? "New observations cannot be added to a completed round"
          : "You do not have permission to create Safety Observation items",
      );
      return;
    }
    setSafeObservations([
      ...safeObservations,
      {
        observationType: "Safe Act",
        categoryId: "",
        subCategoryId: "",
        description: "",
        observerType: "Company Employee",
        observerName: "",
        observerUserId: "",
        safetyScore: "",
        contractorAgencyName: "",
        attachments: [],
      },
    ]);
  };

  const removeSafeObservation = (index) => {
    if (!canDeleteItem) {
      setError("You do not have permission to delete Safety Observation items");
      return;
    }
    setSafeObservations(safeObservations.filter((_, i) => i !== index));
  };

  const updateSafeObservation = async (index, field, value) => {
    if (!canEditItem) {
      setError("You do not have permission to edit Safety Observation items");
      return;
    }
    const updated = [...safeObservations];
    updated[index] = { ...updated[index], [field]: value };

    // If category changed, clear subcategory selection
    if (field === "categoryId") {
      updated[index].subCategoryId = "";
    }

    setSafeObservations(updated);
  };

  const addUnsafeObservation = () => {
    if (!canCreateItem || isCompleted) {
      setError(
        isCompleted
          ? "New observations cannot be added to a completed round"
          : "You do not have permission to create Safety Observation items",
      );
      return;
    }
    setUnsafeObservations([
      ...unsafeObservations,
      {
        itemId: null,
        observationType: "Unsafe Act",
        categoryId: "",
        subCategoryId: "",
        description: "",
        observerType: "Company Employee",
        observerName: "",
        observerUserId: "",
        actionDescription: "",
        responsiblePersonId: "",
        targetCompletionTime: "",
        riskLevel: "",
        safetyScore: "",
        itemStatus: "Open",
        contractorAgencyName: "",
        linkedCapaId: null,
        attachments: [],
      },
    ]);
  };

  const removeUnsafeObservation = (index) => {
    if (!canDeleteItem) {
      setError("You do not have permission to delete Safety Observation items");
      return;
    }
    setUnsafeObservations(unsafeObservations.filter((_, i) => i !== index));
  };

  const updateUnsafeObservation = async (index, field, value) => {
    if (!canEditItem) {
      setError("You do not have permission to edit Safety Observation items");
      return;
    }
    const updated = [...unsafeObservations];
    updated[index] = { ...updated[index], [field]: value };

    // If category changed, clear subcategory selection
    if (field === "categoryId") {
      updated[index].subCategoryId = "";
    }

    setUnsafeObservations(updated);
  };

  const handleImageUpload = async (type, index, files) => {
    if (!files || files.length === 0) return;

    try {
      setSaving(true);
      const updated =
        type === "safe" ? [...safeObservations] : [...unsafeObservations];
      const itemsAttachments = [...(updated[index].attachments || [])];

      for (let i = 0; i < files.length; i++) {
        const result = await safetyObservationService.uploadAttachment(
          files[i],
        );
        itemsAttachments.push({
          fileName: result.fileName,
          fileType: result.fileType,
          fileUrl: result.fileUrl,
        });
      }

      updated[index].attachments = itemsAttachments;
      if (type === "safe") {
        setSafeObservations(updated);
      } else {
        setUnsafeObservations(updated);
      }
    } catch (err) {
      console.error("Error uploading images:", err);
      setError("Failed to upload images. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const removeAttachment = (type, itemIndex, attachmentIndex) => {
    const updated =
      type === "safe" ? [...safeObservations] : [...unsafeObservations];
    const itemsAttachments = [...(updated[itemIndex].attachments || [])];
    itemsAttachments.splice(attachmentIndex, 1);
    updated[itemIndex].attachments = itemsAttachments;

    if (type === "safe") {
      setSafeObservations(updated);
    } else {
      setUnsafeObservations(updated);
    }
  };

  const getSubCategoriesForItem = (type, index, categoryId) => {
    if (!categoryId) return [];
    const category = categories.find(
      (c) => c.categoryId === parseInt(categoryId, 10),
    );
    return category?.subCategories || [];
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.observationDate)
      errors.observationDate = "Observation date is required";
    if (!formData.plantId) errors.plantId = "Plant is required";

    // Workforce Validation
    const companyCount = parseInt(formData.companyEmployeeCount || "0", 10);
    const contractorCount = parseInt(
      formData.contractorEmployeeCount || "0",
      10,
    );

    if (isNaN(companyCount) || companyCount < 0) {
      errors.companyEmployeeCount = "Invalid count";
    }
    if (isNaN(contractorCount) || contractorCount < 0) {
      errors.contractorEmployeeCount = "Invalid count";
    }

    if (companyCount === 0 && contractorCount === 0) {
      errors.workforce = "At least one employee or contractor is required";
    }

    if (observers.length === 0) {
      errors.observers = "At least one observer is required";
    } else {
      observers.forEach((obs, idx) => {
        if (!obs.observerUserId)
          errors[`observer_${idx}`] = "Observer user is required";
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    // Check permissions before submitting
    if (isEditMode && !canEdit) {
      setError("You do not have permission to edit Safety Observation rounds");
      return;
    }
    if (!isEditMode && !canCreate) {
      setError(
        "You do not have permission to create Safety Observation rounds",
      );
      return;
    }

    if (!validateForm()) {
      setError("Please fix the errors before submitting.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const allItems = [
        ...safeObservations.map((item) => ({
          ...item,
          observationType: item.observationType,
        })),
        ...unsafeObservations.map((item) => ({
          ...item,
          observationType: item.observationType,
        })),
      ];

      // Helper function to convert empty string to null for nullable ints
      const toNullableInt = (value) => {
        if (!value || value === "" || value === "0") return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      // Helper function to convert time string to TimeSpan format (HH:mm:ss)
      const convertTimeToTimeSpan = (timeString) => {
        if (!timeString || timeString === "") return null;
        // TimeSpan format: "HH:mm:ss" or "HH:mm"
        // If time is in "HH:mm" format, add ":00" for seconds
        if (timeString.length === 5) {
          return `${timeString}:00`;
        }
        return timeString;
      };

      const roundData = {
        observationDate: formData.observationDate,
        observationTime: convertTimeToTimeSpan(formData.observationTime),
        endTime: convertTimeToTimeSpan(formData.endTime),
        observationDuration: toNullableInt(formData.observationDuration),
        actionSlaTime: toNullableInt(formData.actionSlaTime),
        plantId: toNullableInt(formData.plantId),
        departmentId: toNullableInt(formData.departmentId),
        lineId: toNullableInt(formData.lineId),
        area: formData.area || null,
        createdBy: currentUser?.userId || null,
        companyEmployeeCount: toNullableInt(formData.companyEmployeeCount),
        contractorEmployeeCount: toNullableInt(
          formData.contractorEmployeeCount,
        ),
        companyEmployeeNames: addNames ? formData.companyEmployeeNames : [],
        contractorEmployeeNames: addNames
          ? formData.contractorEmployeeNames
          : [],
        observationItems: allItems.map((item, idx) => {
          const isUnsafe =
            item.observationType === "Unsafe Act" ||
            item.observationType === "Unsafe Condition";
          const itemData = {
            observationType: item.observationType,
            categoryId: parseInt(item.categoryId, 10),
            subCategoryId: item.subCategoryId
              ? parseInt(item.subCategoryId, 10)
              : null,
            description: item.description,
            observerType: item.observerType,
            observerName: item.observerName,
            observerUserId: toNullableInt(item.observerUserId),
            contractorAgencyName: item.contractorAgencyName || null,
            actionDescription: item.actionDescription || null,
            responsiblePersonId: toNullableInt(item.responsiblePersonId),
            targetCompletionTime: item.targetCompletionTime || null,
            safetyScore: item.safetyScore ? parseFloat(item.safetyScore) : null,
            itemOrder: idx,
            attachments: item.attachments
              ? item.attachments.map((a) => ({
                  attachmentId: a.attachmentId,
                  fileName: a.fileName,
                  fileType: a.fileType,
                  fileUrl: a.fileUrl,
                }))
              : [],
          };

          // Only include RiskLevel and ItemStatus for unsafe observations
          // For safe observations, don't include these fields at all (or set to null)
          if (isUnsafe) {
            itemData.riskLevel = item.riskLevel || null;
            itemData.itemStatus = item.itemStatus || "Open"; // Default to 'Open' if not set
            itemData.linkedCapaId = item.linkedCapaId || null; // Ensure LinkedCapaId is passed
          }
          // For safe observations, we don't add riskLevel or itemStatus fields

          return itemData;
        }),
        observers: observers.map((obs) => ({
          observerUserId: parseInt(obs.observerUserId, 10),
          observerRole: obs.observerRole || null,
        })),
      };

      if (isEditMode) {
        roundData.roundId = parseInt(id, 10);
        await safetyObservationService.updateSafetyObservationRound(
          id,
          roundData,
        );
        setSuccess("Safety Observation round updated successfully!");
      } else {
        await safetyObservationService.createSafetyObservationRound(roundData);
        setSuccess("Safety Observation round created successfully!");
      }

      setTimeout(() => {
        navigate("/home/safety-observation");
      }, 1500);
    } catch (err) {
      console.error("Error saving observation round:", err);
      setError(
        err.response?.data?.message ||
          "Failed to save observation round. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer  safety-observation-entry-page">
        <PageLoading message="Loading observation…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer   safety-observation-entry-page">
      <div className="page-header">
        <h1>
          {isEditMode ? "Edit" : "New"} Safety Observation Round
          {isEditMode && formData.statusName && (
            <span
              className={`status-badge ${formData.statusName.toLowerCase()}`}
            >
              {formData.statusName}
            </span>
          )}
        </h1>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/home/safety-observation")}
        >
          <HiOutlineXMark /> Cancel
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {success && <div className="alert alert-success">{success}</div>}

      <form
        className="compact-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Observed Details */}
        <div className="form-section">
          <h2 className="section-title">Observed Details</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Observation Date *</label>
              <input
                type="date"
                name="observationDate"
                value={formData.observationDate}
                onChange={handleInputChange}
                className={formErrors.observationDate ? "error" : ""}
              />
              {formErrors.observationDate && (
                <span className="error-text">{formErrors.observationDate}</span>
              )}
            </div>

            <div className="form-group">
              <label>Start Time</label>
              <input
                type="time"
                name="observationTime"
                value={formData.observationTime}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>End Time</label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Action SLA (min)</label>
              <input
                type="number"
                name="actionSlaTime"
                value={formData.actionSlaTime}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                name="observationDuration"
                value={formData.observationDuration}
                readOnly
                placeholder="Auto-calculated"
              />
            </div>
            <div className="form-group">
              <label>Plant *</label>
              <select
                name="plantId"
                value={formData.plantId}
                onChange={handleInputChange}
                className={formErrors.plantId ? "error" : ""}
              >
                <option value="">Select Plant</option>
                {plants.map((p) => (
                  <option key={p.plantId} value={p.plantId}>
                    {p.plantName}
                  </option>
                ))}
              </select>
              {formErrors.plantId && (
                <span className="error-text">{formErrors.plantId}</span>
              )}
            </div>
            <div className="form-group">
              <label>Department</label>
              <select
                name="departmentId"
                value={formData.departmentId}
                onChange={handleInputChange}
              >
                <option value="">Select Department</option>
                {departments.map((d) => (
                  <option key={d.departmentId} value={d.departmentId}>
                    {d.departmentName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Observed At */}
        <div className="form-section">
          <h2 className="section-title">Observed At</h2>
          <div className="form-row" style={{ flexWrap: "wrap" }}>
            <div className="form-group">
              <label>Line</label>
              <select
                name="lineId"
                value={formData.lineId}
                onChange={handleInputChange}
              >
                <option value="">Select Line</option>
                {lines
                  .filter(
                    (l) =>
                      !formData.plantId ||
                      l.plantId === parseInt(formData.plantId, 10),
                  )
                  .map((l) => (
                    <option key={l.lineId} value={l.lineId}>
                      {l.lineName}
                    </option>
                  ))}
              </select>
            </div>

            {!formData.plantId ? (
              <div className="form-group full-width">
                <label>Area / Facility</label>
                <div
                  className="info-box"
                  style={{
                    padding: "10px",
                    backgroundColor: "#f8f9fa",
                    border: "1px dashed #ced4da",
                    borderRadius: "4px",
                    color: "#6c757d",
                  }}
                >
                  Please select a Plant to view Facility options.
                </div>
              </div>
            ) : siteLayout && siteLayout.level1 ? (
              [1, 2, 3, 4, 5, 6]
                .filter(
                  (level) =>
                    !sboSettings.facilityLevel ||
                    level <= parseInt(sboSettings.facilityLevel),
                )
                .map((level) => {
                  const levelName = siteLayout[`level${level}`];
                  if (!levelName) return null;

                  const allLevelItems = layoutItems[level] || [];
                  let items = allLevelItems;

                  if (level > 1) {
                    const parentId = selectedLayout[level - 1];
                    if (!parentId) {
                      items = [];
                    } else {
                      items = allLevelItems.filter(
                        (item) => String(item.parentId) === String(parentId),
                      );
                    }
                  }

                  const isDisabled = level > 1 && !selectedLayout[level - 1];

                  return (
                    <div className="form-group" key={level}>
                      <label>{levelName}</label>
                      <select
                        value={selectedLayout[level] || ""}
                        onChange={(e) =>
                          handleLayoutChange(level, e.target.value)
                        }
                        disabled={isDisabled}
                      >
                        <option value="">Select {levelName}</option>
                        {items.map((item) => (
                          <option
                            key={item.id || item.itemId}
                            value={item.id || item.itemId}
                          >
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })
            ) : null}
          </div>
        </div>

        {/* Workforce Presence */}
        <div className="form-section">
          <h2 className="section-title">Workforce Presence</h2>
          {formErrors.workforce && (
            <div className="alert alert-error" style={{ marginBottom: "10px" }}>
              {formErrors.workforce}
            </div>
          )}

          <div className="form-row">
            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <label
                className="checkbox-label"
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={addNames}
                  onChange={(e) => {
                    setAddNames(e.target.checked);
                    if (!e.target.checked) {
                      // Reset names but keep counts if switched off?
                      // Requirements: "When OFF: Hide name fields. Allow manual entry."
                      // We keep the counts as they were.
                    }
                  }}
                  style={{ marginRight: "8px", width: "auto", height: "auto" }}
                />
                <span>Add Names</span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Number of Company Employees *</label>
              <input
                type="number"
                name="companyEmployeeCount"
                value={formData.companyEmployeeCount}
                onChange={handleInputChange}
                min="0"
                readOnly={addNames}
                className={formErrors.companyEmployeeCount ? "error" : ""}
              />
            </div>
            <div className="form-group">
              <label>Number of Contractor Employees *</label>
              <input
                type="number"
                name="contractorEmployeeCount"
                value={formData.contractorEmployeeCount}
                onChange={handleInputChange}
                min="0"
                readOnly={addNames}
                className={formErrors.contractorEmployeeCount ? "error" : ""}
              />
            </div>
          </div>

          {addNames && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Company Employee Names</label>
                <select
                  multiple
                  className="form-control"
                  value={formData.companyEmployeeNames}
                  onChange={(e) =>
                    handleNameSelection(e, "companyEmployeeNames")
                  }
                  style={{ height: "120px", padding: "8px" }}
                >
                  {users &&
                    users.map((u) => (
                      <option
                        key={u.userId}
                        value={u.fullName || u.userName || u.username}
                      >
                        {u.fullName || u.userName || u.username}
                      </option>
                    ))}
                </select>
                <small className="help-text" style={{ color: "#666" }}>
                  Hold Ctrl/Cmd to select multiple
                </small>
              </div>
              <div className="form-group full-width">
                <label>Contractor Employee Names</label>
                <select
                  multiple
                  className="form-control"
                  value={formData.contractorEmployeeNames}
                  onChange={(e) =>
                    handleNameSelection(e, "contractorEmployeeNames")
                  }
                  style={{ height: "120px", padding: "8px" }}
                >
                  {agencies &&
                    [
                      ...new Set(
                        agencies.flatMap((a) => a.contractorNames || []),
                      ),
                    ].map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                </select>
                <small className="help-text" style={{ color: "#666" }}>
                  Hold Ctrl/Cmd to select multiple
                </small>
              </div>
            </div>
          )}
        </div>

        {/* Observers */}
        <div className="form-section">
          <div className="section-header">
            <h2 className="section-title">Observers</h2>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={addObserver}
            >
              <HiOutlinePlus /> Add Observer
            </button>
          </div>
          {formErrors.observers && (
            <span className="error-text">{formErrors.observers}</span>
          )}
          {observers.map((observer, index) => (
            <div key={index} className="compact-item">
              <div className="form-group">
                <label>Observer *</label>
                <select
                  value={observer.observerUserId || ""}
                  onChange={(e) =>
                    updateObserver(index, "observerUserId", e.target.value)
                  }
                  className={formErrors[`observer_${index}`] ? "error" : ""}
                >
                  <option value="">Select User</option>
                  {users && users.length > 0 ? (
                    users.map((u) => (
                      <option key={u.userId} value={String(u.userId)}>
                        {u.fullName ||
                          u.userName ||
                          u.username ||
                          `User ${u.userId}`}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No users available
                    </option>
                  )}
                </select>
                {formErrors[`observer_${index}`] && (
                  <span className="error-text">
                    {formErrors[`observer_${index}`]}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Role</label>
                <input
                  type="text"
                  value={observer.observerRole}
                  onChange={(e) =>
                    updateObserver(index, "observerRole", e.target.value)
                  }
                  placeholder="e.g., Lead Observer"
                />
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeObserver(index)}
              >
                <HiOutlineTrash />
              </button>
            </div>
          ))}
          {observers.length === 0 && (
            <p className="empty-message">
              No observers added. Click "Add Observer" to add one.
            </p>
          )}
        </div>

        {/* Safe Observations */}
        <div className="form-section safeobservations ">
          <div className="section-header">
            <h2 className="section-title">Safe Observations</h2>
            {canCreateItem && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={addSafeObservation}
              >
                <HiOutlinePlus /> Add Observation
              </button>
            )}
          </div>
          {canViewItem &&
            safeObservations.map((observation, index) => (
              <div key={index} className="compact-item">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={observation.observationType}
                    onChange={(e) =>
                      updateSafeObservation(
                        index,
                        "observationType",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="Safe Act">Safe Act</option>
                    <option value="Safe Condition">Safe Condition</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Observer Type</label>
                  <select
                    value={observation.observerType || "Company Employee"}
                    onChange={(e) =>
                      updateSafeObservation(
                        index,
                        "observerType",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="Company Employee">Company Employee</option>
                    <option value="Contractor Employee">
                      Contractor Employee
                    </option>
                  </select>
                </div>

                {observation.observerType === "Company Employee" ? (
                  <div className="form-group">
                    <label>Observer Name</label>
                    <select
                      value={observation.observerUserId || ""}
                      onChange={(e) => {
                        const selectedUser = users.find(
                          (u) => String(u.userId) === e.target.value,
                        );
                        updateSafeObservation(
                          index,
                          "observerUserId",
                          e.target.value,
                        );
                        if (selectedUser)
                          updateSafeObservation(
                            index,
                            "observerName",
                            selectedUser.fullName,
                          );
                      }}
                      disabled={!canEditItem}
                    >
                      <option value="">Select User</option>
                      {users && users.length > 0 ? (
                        users.map((u) => (
                          <option key={u.userId} value={String(u.userId)}>
                            {u.fullName || u.userName || u.username}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No users available
                        </option>
                      )}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Contractor Agency Name</label>
                      <select
                        value={observation.contractorAgencyName || ""}
                        onChange={(e) => {
                          updateSafeObservation(
                            index,
                            "contractorAgencyName",
                            e.target.value,
                          );
                          updateSafeObservation(index, "observerName", "");
                        }}
                        disabled={!canEditItem}
                      >
                        <option value="">Select Agency</option>
                        {agencies.map((a) => (
                          <option key={a.agencyId} value={a.agencyName}>
                            {a.agencyName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Contractor Employee Name</label>
                      <select
                        value={observation.observerName || ""}
                        onChange={(e) =>
                          updateSafeObservation(
                            index,
                            "observerName",
                            e.target.value,
                          )
                        }
                        disabled={
                          !canEditItem || !observation.contractorAgencyName
                        }
                      >
                        <option value="">Select Contractor</option>
                        {(
                          agencies.find(
                            (a) =>
                              a.agencyName === observation.contractorAgencyName,
                          )?.contractorNames || []
                        ).map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={
                      observation.categoryId
                        ? String(observation.categoryId)
                        : ""
                    }
                    onChange={(e) =>
                      updateSafeObservation(index, "categoryId", e.target.value)
                    }
                    disabled={!canEditItem}
                  >
                    <option value="">Select Category</option>
                    {categories && categories.length > 0 ? (
                      categories.map((c) => (
                        <option key={c.categoryId} value={String(c.categoryId)}>
                          {c.categoryName}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No categories available
                      </option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Subcategory</label>
                  <select
                    value={
                      observation.subCategoryId
                        ? String(observation.subCategoryId)
                        : ""
                    }
                    onChange={(e) =>
                      updateSafeObservation(
                        index,
                        "subCategoryId",
                        e.target.value,
                      )
                    }
                    disabled={!observation.categoryId || !canEditItem}
                  >
                    <option value="">Select Subcategory</option>
                    {getSubCategoriesForItem(
                      "safe",
                      index,
                      observation.categoryId,
                    ).map((sc) => (
                      <option
                        key={sc.subCategoryId}
                        value={String(sc.subCategoryId)}
                      >
                        {sc.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Safety Score</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={observation.safetyScore}
                    onChange={(e) =>
                      updateSafeObservation(
                        index,
                        "safetyScore",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={observation.description}
                    onChange={(e) =>
                      updateSafeObservation(
                        index,
                        "description",
                        e.target.value,
                      )
                    }
                    rows={2}
                    disabled={!canEditItem}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Attachments (Images)</label>
                  <div className="attachments-container">
                    <div className="attachment-upload">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload("safe", index, e.target.files)
                        }
                        id={`safe-upload-${index}`}
                        style={{ display: "none" }}
                        disabled={!canEditItem}
                      />
                      <label
                        htmlFor={`safe-upload-${index}`}
                        className="upload-label"
                      >
                        <HiOutlinePhoto /> Upload Images
                      </label>
                    </div>
                    <div className="attachment-list">
                      {observation.attachments &&
                        observation.attachments.map((att, attIdx) => (
                          <div key={attIdx} className="attachment-preview">
                            <img src={att.fileUrl} alt={att.fileName} />
                            <button
                              type="button"
                              className="remove-att"
                              onClick={() =>
                                removeAttachment("safe", index, attIdx)
                              }
                            >
                              <HiOutlineXMark />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                {canDeleteItem && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeSafeObservation(index)}
                  >
                    <HiOutlineTrash />
                  </button>
                )}
              </div>
            ))}
          {safeObservations.length === 0 && canViewItem && (
            <p className="empty-message">
              {canCreateItem
                ? 'No safe observations added. Click "Add Observation" to add one.'
                : "No safe observations added."}
            </p>
          )}
          {!canViewItem && (
            <p className="empty-message">
              You do not have permission to view Safety Observation items.
            </p>
          )}
        </div>

        {/* Unsafe Observations */}
        <div className="form-section unsafeobservations">
          <div className="section-header">
            <h2 className="section-title">Unsafe Observations</h2>
            {canCreateItem && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={addUnsafeObservation}
              >
                <HiOutlinePlus /> Add Observation
              </button>
            )}
          </div>
          {canViewItem &&
            unsafeObservations.map((observation, index) => (
              <div key={index} className="compact-item">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={observation.observationType}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "observationType",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="Unsafe Act">Unsafe Act</option>
                    <option value="Unsafe Condition">Unsafe Condition</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Observer Type</label>
                  <select
                    value={observation.observerType || "Company Employee"}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "observerType",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="Company Employee">Company Employee</option>
                    <option value="Contractor Employee">
                      Contractor Employee
                    </option>
                  </select>
                </div>

                {observation.observerType === "Company Employee" ? (
                  <div className="form-group">
                    <label>Observer Name</label>
                    <select
                      value={observation.observerUserId || ""}
                      onChange={(e) => {
                        const selectedUser = users.find(
                          (u) => String(u.userId) === e.target.value,
                        );
                        updateUnsafeObservation(
                          index,
                          "observerUserId",
                          e.target.value,
                        );
                        if (selectedUser)
                          updateUnsafeObservation(
                            index,
                            "observerName",
                            selectedUser.fullName,
                          );
                      }}
                      disabled={!canEditItem}
                    >
                      <option value="">Select User</option>
                      {users && users.length > 0 ? (
                        users.map((u) => (
                          <option key={u.userId} value={String(u.userId)}>
                            {u.fullName || u.userName || u.username}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No users available
                        </option>
                      )}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Contractor Agency Name</label>
                      <select
                        value={observation.contractorAgencyName || ""}
                        onChange={(e) => {
                          updateUnsafeObservation(
                            index,
                            "contractorAgencyName",
                            e.target.value,
                          );
                          updateUnsafeObservation(index, "observerName", "");
                        }}
                        disabled={!canEditItem}
                      >
                        <option value="">Select Agency</option>
                        {agencies.map((a) => (
                          <option key={a.agencyId} value={a.agencyName}>
                            {a.agencyName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Contractor Employee Name</label>
                      <select
                        value={observation.observerName || ""}
                        onChange={(e) =>
                          updateUnsafeObservation(
                            index,
                            "observerName",
                            e.target.value,
                          )
                        }
                        disabled={
                          !canEditItem || !observation.contractorAgencyName
                        }
                      >
                        <option value="">Select Contractor</option>
                        {(
                          agencies.find(
                            (a) =>
                              a.agencyName === observation.contractorAgencyName,
                          )?.contractorNames || []
                        ).map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={
                      observation.categoryId
                        ? String(observation.categoryId)
                        : ""
                    }
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "categoryId",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="">Select Category</option>
                    {categories && categories.length > 0 ? (
                      categories.map((c) => (
                        <option key={c.categoryId} value={String(c.categoryId)}>
                          {c.categoryName}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No categories available
                      </option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Subcategory</label>
                  <select
                    value={
                      observation.subCategoryId
                        ? String(observation.subCategoryId)
                        : ""
                    }
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "subCategoryId",
                        e.target.value,
                      )
                    }
                    disabled={!observation.categoryId || !canEditItem}
                  >
                    <option value="">Select Subcategory</option>
                    {getSubCategoriesForItem(
                      "unsafe",
                      index,
                      observation.categoryId,
                    ).map((sc) => (
                      <option
                        key={sc.subCategoryId}
                        value={String(sc.subCategoryId)}
                      >
                        {sc.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Risk Level</label>
                  <select
                    value={observation.riskLevel}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "riskLevel",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="">Select Risk Level</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={observation.itemStatus}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "itemStatus",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  >
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Safety Score</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={observation.safetyScore}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "safetyScore",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  />
                </div>
                {observation.itemId && observation.linkedCapaId && (
                  <div className="form-group">
                    <label>Linked CAPA</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          color: "var(--accent-primary)",
                          fontWeight: "500",
                        }}
                      >
                        CAPA-{observation.linkedCapaId}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          navigate(
                            `/home/capa-entry/${observation.linkedCapaId}`,
                          )
                        }
                      >
                        View CAPA
                      </button>
                    </div>
                  </div>
                )}
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={observation.description}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "description",
                        e.target.value,
                      )
                    }
                    rows={2}
                    disabled={!canEditItem}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Attachments (Images)</label>
                  <div className="attachments-container">
                    <div className="attachment-upload">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload("unsafe", index, e.target.files)
                        }
                        id={`unsafe-upload-${index}`}
                        style={{ display: "none" }}
                        disabled={!canEditItem}
                      />
                      <label
                        htmlFor={`unsafe-upload-${index}`}
                        className="upload-label"
                      >
                        <HiOutlinePhoto /> Upload Images
                      </label>
                    </div>
                    <div className="attachment-list">
                      {observation.attachments &&
                        observation.attachments.map((att, attIdx) => (
                          <div key={attIdx} className="attachment-preview">
                            <img src={att.fileUrl} alt={att.fileName} />
                            <button
                              type="button"
                              className="remove-att"
                              onClick={() =>
                                removeAttachment("unsafe", index, attIdx)
                              }
                            >
                              <HiOutlineXMark />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Action Description *</label>
                  <textarea
                    value={observation.actionDescription || ""}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "actionDescription",
                        e.target.value,
                      )
                    }
                    rows={2}
                    disabled={!canEditItem}
                    className="action-description-input"
                  />
                </div>

                <div className="form-group">
                  <label>Responsible Person</label>
                  <select
                    value={observation.responsiblePersonId || ""}
                    onChange={(e) => {
                      const selectedUser = users.find(
                        (u) => String(u.userId) === e.target.value,
                      );
                      updateUnsafeObservation(
                        index,
                        "responsiblePersonId",
                        e.target.value,
                      );
                      if (selectedUser)
                        updateUnsafeObservation(
                          index,
                          "responsiblePerson",
                          selectedUser.fullName,
                        );
                    }}
                    disabled={!canEditItem}
                  >
                    <option value="">Select User</option>
                    {users.map((u) => (
                      <option key={u.userId} value={String(u.userId)}>
                        {u.fullName || u.userName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Target Date</label>
                  <input
                    type="date"
                    value={observation.targetCompletionTime || ""}
                    onChange={(e) =>
                      updateUnsafeObservation(
                        index,
                        "targetCompletionTime",
                        e.target.value,
                      )
                    }
                    disabled={!canEditItem}
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center" }}>
                    Resolved on Spot?
                  </label>
                  <div
                    className="toggle-switch-container"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "5px",
                    }}
                  >
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={observation.itemStatus === "Closed"}
                        onChange={(e) =>
                          updateUnsafeObservation(
                            index,
                            "itemStatus",
                            e.target.checked ? "Closed" : "Open",
                          )
                        }
                        disabled={!canEditItem}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span
                      style={{
                        fontSize: "0.9rem",
                        color:
                          observation.itemStatus === "Closed"
                            ? "var(--success-color)"
                            : "var(--warning-color)",
                        fontWeight: "500",
                      }}
                    >
                      {observation.itemStatus === "Closed"
                        ? "Yes (Closed)"
                        : "No (Open - CAPA will be created)"}
                    </span>
                  </div>
                </div>
                {canDeleteItem && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeUnsafeObservation(index)}
                  >
                    <HiOutlineTrash />
                  </button>
                )}
              </div>
            ))}
          {unsafeObservations.length === 0 && canViewItem && (
            <p className="empty-message">
              {canCreateItem
                ? 'No unsafe observations added. Click "Add Observation" to add one.'
                : "No unsafe observations added."}
            </p>
          )}
          {!canViewItem && (
            <p className="empty-message">
              You do not have permission to view Safety Observation items.
            </p>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/home/safety-observation")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || isCompleted}
          >
            {saving ? "Saving..." : isEditMode ? "Update" : "Create"}{" "}
            Observation Round
          </button>
        </div>
      </form>
    </div>
  );
};

export default SafetyObservationEntry;
